import type { ProjectRepoResource, UpdateRepoResourcesRequest } from '@protoss/shared';
import { apiFetch } from '../../../lib/apiClient';
import type {
  BranchOption,
  CommitHistoryResponse,
  LinkedRepoOption,
  PipelineCategory,
  PipelineIntegrationOption,
  PipelineOption,
  PipelineRunsResponse,
  RepoBrowserFileDTO,
  RepoBrowserTreeDTO,
  RepoIntegrationOption,
  RepoOption,
  SaveScheduleRequest,
  SaveSourceSystemSettingsRequest,
  SourceSystemPanelState,
  SourceSystemSchedule,
  SourceSystemSettingsDTO,
  SourceType,
  SyncScope,
  TriggerSyncResponse,
} from '../types/source-system';

const BASE = '/api/project';

export const sourceSystemApi = {
  getPanelState(projectUuid: string, sourceType: SourceType): Promise<SourceSystemPanelState> {
    return apiFetch(`${BASE}/${projectUuid}/systems/${sourceType}`);
  },

  getSchedule(projectUuid: string, sourceType: SourceType): Promise<SourceSystemSchedule | null> {
    return apiFetch(`${BASE}/${projectUuid}/systems/${sourceType}/schedule`);
  },

  saveSchedule(
    projectUuid: string,
    sourceType: SourceType,
    body: SaveScheduleRequest
  ): Promise<SourceSystemSchedule> {
    return apiFetch(`${BASE}/${projectUuid}/systems/${sourceType}/schedule`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  saveRepoResources(
    projectUuid: string,
    bindingUuid: string,
    body: UpdateRepoResourcesRequest
  ): Promise<{ repos: ProjectRepoResource[] }> {
    return apiFetch(`/api/project/${projectUuid}/integration/${bindingUuid}/repo-resources`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  triggerSync(
    projectUuid: string,
    resourceUuid: string,
    syncScope?: SyncScope
  ): Promise<TriggerSyncResponse> {
    return apiFetch(`${BASE}/${projectUuid}/source/${resourceUuid}/sync`, {
      method: 'POST',
      body: syncScope ? JSON.stringify({ syncScope }) : undefined,
    });
  },

  getSettings(projectUuid: string, sourceType: SourceType): Promise<SourceSystemSettingsDTO> {
    return apiFetch(`${BASE}/${projectUuid}/systems/${sourceType}/settings`);
  },

  saveSettings(
    projectUuid: string,
    sourceType: SourceType,
    body: SaveSourceSystemSettingsRequest
  ): Promise<SourceSystemSettingsDTO> {
    return apiFetch(`${BASE}/${projectUuid}/systems/${sourceType}/settings`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  getAvailableRepos(
    projectUuid: string,
    sourceType: SourceType
  ): Promise<{ repos: Array<{ id: string; name: string; parentId: string | null }> }> {
    return apiFetch(`${BASE}/${projectUuid}/systems/${sourceType}/available-repos`);
  },

  getRepoIntegrations(projectUuid: string): Promise<{ integrations: RepoIntegrationOption[] }> {
    return apiFetch(`${BASE}/${projectUuid}/systems/repo/integrations`);
  },

  getPipelineIntegrations(
    projectUuid: string
  ): Promise<{ integrations: PipelineIntegrationOption[] }> {
    return apiFetch(`${BASE}/${projectUuid}/systems/pipeline/integrations`);
  },

  getPipelinesForIntegration(
    projectUuid: string,
    bindingUuid: string,
    category: PipelineCategory
  ): Promise<{ pipelines: PipelineOption[] }> {
    const params = new URLSearchParams({ category });
    return apiFetch(
      `${BASE}/${projectUuid}/systems/pipeline/integrations/${bindingUuid}/pipelines?${params.toString()}`
    );
  },

  getRepoBrowserTree(projectUuid: string): Promise<RepoBrowserTreeDTO> {
    return apiFetch(`${BASE}/${projectUuid}/systems/repo/browser/tree`);
  },

  getRepoBrowserFile(projectUuid: string, nodeId: string): Promise<RepoBrowserFileDTO> {
    const params = new URLSearchParams({ nodeId });
    return apiFetch(`${BASE}/${projectUuid}/systems/repo/browser/file?${params.toString()}`);
  },

  getReposForIntegration(
    projectUuid: string,
    bindingUuid: string
  ): Promise<{ repos: RepoOption[] }> {
    return apiFetch(`${BASE}/${projectUuid}/systems/repo/integrations/${bindingUuid}/repos`);
  },

  getBranchesForRepo(
    projectUuid: string,
    bindingUuid: string,
    repoId: string,
    parentId: string | null
  ): Promise<{ branches: BranchOption[] }> {
    const params = new URLSearchParams({ repoId });
    if (parentId) params.set('parentId', parentId);
    return apiFetch(
      `${BASE}/${projectUuid}/systems/repo/integrations/${bindingUuid}/branches?${params.toString()}`
    );
  },

  getCommitHistory(
    projectUuid: string,
    page: number,
    pageSize: number
  ): Promise<CommitHistoryResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    return apiFetch(`${BASE}/${projectUuid}/systems/repo/commits?${params.toString()}`);
  },

  getPipelineRuns(
    projectUuid: string,
    resourceUuid: string,
    limit = 20
  ): Promise<PipelineRunsResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    return apiFetch(
      `${BASE}/${projectUuid}/systems/pipeline/resources/${resourceUuid}/runs?${params.toString()}`
    );
  },

  getPipelineLinkedRepos(projectUuid: string): Promise<{ repos: LinkedRepoOption[] }> {
    return apiFetch(`${BASE}/${projectUuid}/systems/pipeline/linked-repos`);
  },
};
